import { LOG_LEVELS, logMessage } from '../logging.js';
import { sendAuthenticatedRequest } from '../auth.js';
import { CardDisplayPopupController } from '../controllers/popups/card_display_popup_controller.js';
import stateManager from '../data/state_manager.js';
import config from '../../config/index.js';
import { canUseMultiCollection } from '../utils/subscription_utils.js';
import { getItemPrice, computeBestPrice } from '../utils/price_utils.js';

class CardGrid {
  constructor(tour) {
    this.gridSelector = '.card-grid';
    this.titleContainerSelector = '.collection-title-container';
    this.tour = tour;
  }

  /**
   * Fetches cards from the server.
   * @param {Object} headers - The headers for the authenticated request.
   * @returns {Promise<Object>} - The fetched card data.
   */
  async fetchCards(headers) {
    try {
      const response = await sendAuthenticatedRequest(
        `${config.detectionUrl}/get-cards`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const error = `HTTP error! status: ${response.status}, message: ${response.body.error}`;
        logMessage(LOG_LEVELS.ERROR, error);
        throw new Error(error);
      }

      logMessage(LOG_LEVELS.INFO, 'Cards fetched successfully', {
        cardCount: response.body.cards.length,
      });

      return response.body;
    } catch (error) {
      logMessage(LOG_LEVELS.ERROR, 'Failed to fetch cards', error.toString());
      throw error;
    }
  }

  /**
   * Fetches and sets the image for an img element.
   * @param {HTMLImageElement} imgElement - The image element to set.
   * @param {string} imageUrl - The URL of the image.
   * @param {string} altText - The alt text for the image.
   * @param {Object} headers - The headers for the authenticated request.
   */
  async getImage(imgElement, imageUrl, altText, headers) {
    logMessage(LOG_LEVELS.DEBUG, 'Fetching and setting image', { imageUrl });

    try {
      const response = await sendAuthenticatedRequest(imageUrl, {
        method: 'GET',
        headers,
        responseType: 'blob',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const localUrl = URL.createObjectURL(response.blob);
      imgElement.src = localUrl;
      imgElement.alt = altText;
    } catch (error) {
      logMessage(
        LOG_LEVELS.ERROR,
        'Error fetching the image:',
        error.toString()
      );
    }
  }

  /**
   * Loads and displays cards on the page.
   * @param {Object} headers - The headers for the authenticated requests.
   * @param {?number} [collectionId=null] - Filter cards by collection when provided.
   */
  async loadCards(headers, collectionId = null) {
    try {
      const { isMobileDevice } = stateManager.getState();
      const data = await this.fetchCards(headers);
      let { cards, matched_items, priced_items, best_prices } = data;

      if (collectionId && canUseMultiCollection()) {
        cards = cards.filter((card) => {
          const collections = card.user_card.collections;
          if (Array.isArray(collections)) {
            return collections.some((c) => c.id === collectionId);
          }
          return Array.isArray(card.user_card.collection_ids)
            ? card.user_card.collection_ids.some((id) => id === collectionId)
            : false;
        });
      }

      // Sort cards so the most recently added card appears first
      cards.sort((a, b) => {
        const idA =
          a.user_card.user_card_id !== undefined
            ? a.user_card.user_card_id
            : a.user_card.id;
        const idB =
          b.user_card.user_card_id !== undefined
            ? b.user_card.user_card_id
            : b.user_card.id;
        return idB - idA;
      });

      const grid = document.querySelector(this.gridSelector);
      grid.innerHTML = ''; // Clear existing cards

      if (!isMobileDevice) {
        const titleContainer = document.querySelector(
          this.titleContainerSelector
        );
        if (titleContainer) {
          titleContainer.style.margin = '0px 0px 20px 0px';
        }
      }

      if (cards.length === 0) {
        stateManager.updateState({ newUser: true });
        const defaultMessage = document.createElement('div');
        defaultMessage.className = 'default-text';
        defaultMessage.textContent = 'Add your first card.';
        grid.appendChild(defaultMessage);
      } else {
        stateManager.updateState({ newUser: false });

        cards.forEach((card, index) => {
          const referenceId = card.reference_card.id;
          let cardBestPrice = best_prices?.[referenceId];
          const activeItems = matched_items?.[referenceId] || [];
          const soldItems = priced_items?.[referenceId] || [];
          let priceSource = null;
          const numericBestPrice = Number(cardBestPrice);
          if (
            cardBestPrice === undefined ||
            cardBestPrice === null ||
            !Number.isFinite(numericBestPrice) ||
            numericBestPrice <= 0
          ) {
            const computed = computeBestPrice(soldItems, activeItems);
            cardBestPrice = computed.price;
            priceSource = computed.source;
          } else {
            cardBestPrice = numericBestPrice;
          }
          const cardElement = this.createCardElement(
            `card_${index}`,
            card,
            activeItems,
            soldItems,
            cardBestPrice,
            priceSource,
            collectionId,
            headers
          );
          grid.appendChild(cardElement);
        });

        const { tour1Started, tour1Completed, tourSkipped } =
          stateManager.getState();

        if (tour1Started && tour1Completed && !tourSkipped) {
          this.tour.enable();
        }
        this.tour.start();
      }

      logMessage(LOG_LEVELS.INFO, 'Cards loaded and displayed');

      const searchInput = document.querySelector('#cardSearch');
      if (searchInput) {
        this.filterCards(searchInput.value);
      }
    } catch (error) {
      logMessage(LOG_LEVELS.ERROR, 'Error loading cards', error.toString());
    }
  }

  /**
   * Creates an image element for a card.
   * @param {Object} cardInfo - The information of the card.
   * @param {Object} headers - The headers for the authenticated request.
   * @returns {HTMLImageElement} - The created image element.
   */
  createImageElement(cardInfo, headers) {
    logMessage(LOG_LEVELS.DEBUG, 'Creating image element for card', {
      cardId: cardInfo.user_card.id,
    });

    const img = document.createElement('img');
    this.getImage(
      img,
      cardInfo.user_card.front_image_url_medium,
      cardInfo.reference_card.identifier,
      headers
    );

    logMessage(LOG_LEVELS.INFO, 'Image element created', {
      cardId: cardInfo.user_card.id,
    });
    return img;
  }

  /**
   * Formats a price into USD currency.
   * @param {number} price - The price to format.
   * @returns {string} - The formatted price.
   */
  formatPrice(price) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return formatter.format(price);
  }

  /**
   * Creates a status element for a card.
   * @param {Object} referenceCard - The reference card data.
   * @param {number} [bestPrice] - The best price for the card, if available.
   * @returns {HTMLDivElement} - The created status element.
   */
  createStatusElement(referenceCard, bestPrice, priceSource = null) {
    const statusStr = referenceCard.tracking_status;
    logMessage(LOG_LEVELS.DEBUG, 'Creating status element for card', {
      statusStr,
    });

    const statusDiv = document.createElement('div');
    const statusMappings = {
      PRICE: { className: 'card-status-match', text: 'PRICE FOUND' },
      MATCH: { className: 'card-status-match', text: 'TRACKING' },
      null: { className: 'card-status-not-found', text: 'NO COMPS FOUND' },
    };

    if (bestPrice !== null && bestPrice !== undefined) {
      statusDiv.classList.add('card-status-match');
      statusDiv.textContent = `${
        priceSource === 'buyNow' ? '< ' : ''
      }${this.formatPrice(bestPrice)}`;
    } else {
      const { className, text } =
        statusMappings[statusStr] || statusMappings['null'];
      statusDiv.classList.add(className);
      statusDiv.textContent = text;
    }

    logMessage(LOG_LEVELS.INFO, 'Status element created', { statusStr });
    return statusDiv;
  }

  /**
   * Creates a title element.
   * @param {string} title - The title text.
   * @returns {HTMLDivElement} - The created title element.
   */
  createTitleElement(title) {
    const titleDiv = document.createElement('div');
    titleDiv.classList.add('card-title');
    titleDiv.textContent = title;

    logMessage(LOG_LEVELS.INFO, 'Title element created');
    return titleDiv;
  }

  /**
   * Creates a subtitle element.
   * @param {string} subtitle - The subtitle text.
   * @returns {HTMLDivElement} - The created subtitle element.
   */
  createSubtitleElement(subtitle) {
    const subtitleDiv = document.createElement('div');
    subtitleDiv.classList.add('card-subtitle');
    subtitleDiv.textContent = subtitle || 'Unknown Team';

    logMessage(LOG_LEVELS.INFO, 'Subtitle element created');
    return subtitleDiv;
  }

  createDescriptionArea(existingDesc, collectionId, cardId, headers) {
    const container = document.createElement('div');
    container.classList.add('description-container');

    const placeholder = document.createElement('div');
    placeholder.classList.add('description-placeholder');
    placeholder.textContent =
      existingDesc && existingDesc.trim().length > 0
        ? existingDesc
        : 'Set Description';

    const input = document.createElement('input');
    input.type = 'text';
    input.classList.add('description-input');
    input.value = existingDesc || '';
    input.style.display = 'none';

    container.appendChild(placeholder);
    container.appendChild(input);

    placeholder.addEventListener('click', (event) => {
      event.stopPropagation();
      placeholder.style.display = 'none';
      input.style.display = 'inline-block';
      input.focus();
    });

    input.addEventListener('keydown', async (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        await commitDescription();
      }
    });

    input.addEventListener('blur', async () => {
      await commitDescription();
    });

    const commitDescription = async () => {
      const newDesc = input.value.trim();
      await this.saveCustomDescription(cardId, collectionId, newDesc, headers);
      placeholder.textContent =
        newDesc.length > 0 ? newDesc : 'Set Description';
      input.style.display = 'none';
      placeholder.style.display = 'inline-block';
    };

    return container;
  }

  async saveCustomDescription(cardId, collectionId, newDescription, headers) {
    try {
      const reqHeaders = { ...headers, 'Content-Type': 'application/json' };
      const result = await sendAuthenticatedRequest(
        `${config.detectionUrl}/save-card-description`,
        {
          method: 'POST',
          headers: reqHeaders,
          body: JSON.stringify({
            card_id: cardId,
            collection_id: collectionId,
            description: newDescription,
          }),
        }
      );

      if (!result.ok) {
        logMessage(
          LOG_LEVELS.ERROR,
          'Error saving description',
          result.body?.error || result.status
        );
      }
    } catch (error) {
      logMessage(
        LOG_LEVELS.ERROR,
        'Error in saveCustomDescription',
        error.toString()
      );
    }
  }

  /**
   * Creates a complete card element.
   * @param {string} id - The unique ID for the card element.
   * @param {Object} cardInfo - The information of the card.
   * @param {Array} matchedItems - The matched items for the card.
   * @param {Array} pricedItems - The priced items for the card.
   * @param {number} bestPrice - The best price for the card.
   * @param {?string} priceSource - Where the price came from ('sold', 'buyNow', 'auction', or null).
   * @param {?number} activeCollectionId - The ID of the collection currently being viewed.
   * @param {Object} headers - The headers for authenticated requests.
   * @returns {HTMLDivElement} - The created card element.
   */
  createCardElement(
    id,
    cardInfo,
    matchedItems,
    pricedItems,
    bestPrice,
    priceSource,
    activeCollectionId,
    headers
  ) {
    logMessage(LOG_LEVELS.DEBUG, 'Creating card element', {
      cardId: cardInfo.reference_card.id,
    });

    const cardDiv = document.createElement('div');
    cardDiv.classList.add('card');
    cardDiv.id = id;

    cardDiv.addEventListener('click', async () => {
      const cardDisplayController = new CardDisplayPopupController(
        this.tour,
        cardInfo,
        false
      );
      logMessage(LOG_LEVELS.INFO, 'Card element clicked', {
        cardId: cardInfo.reference_card.id,
      });
      await cardDisplayController.showCardDisplayPopup(
        matchedItems,
        pricedItems,
        headers
      );
      this.tour.nextStep();
    });

    const titleContentContainer = document.createElement('div');
    titleContentContainer.classList.add('title-content-container');

    const titleText = `${cardInfo.reference_card.players[0]} - ${cardInfo.reference_card.year} ${cardInfo.reference_card.program}`;
    let titleText2 = cardInfo.reference_card.release_set;
    if (cardInfo.reference_card.parallel) {
      titleText2 += ` - ${cardInfo.reference_card.parallel}`;
    }
    let subtitleText = '';
    const teamToShow = cardInfo?.user_card?.team_override || '';
    const positionToShow = cardInfo?.user_card?.position_override || '';
    // If team missing but position present, show "Unknown Team - <position>"
    const teamLabel = teamToShow || (positionToShow ? 'Unknown Team' : '');
    if (teamLabel || positionToShow) {
      subtitleText = positionToShow ? `${teamLabel} - ${positionToShow}` : teamLabel;
    }

    const title = this.createTitleElement(titleText);
    const title2 = this.createTitleElement(titleText2);
    const subtitle = this.createSubtitleElement(subtitleText);

    if (titleText2) {
      titleContentContainer.appendChild(title);
      titleContentContainer.appendChild(title2);
    } else {
      titleContentContainer.appendChild(title2);
      titleContentContainer.appendChild(title);
    }

    titleContentContainer.appendChild(subtitle);

    cardDiv.appendChild(titleContentContainer);

    const contentContainer = document.createElement('div');
    contentContainer.classList.add('card-content-container');

    const img = this.createImageElement(cardInfo, headers);
    contentContainer.appendChild(img);

    const descMsgContainer = document.createElement('div');
    descMsgContainer.classList.add('desc-msg-container');

    const descContainer = document.createElement('div');
    descContainer.classList.add('desc-container');

    const userCard = cardInfo.user_card || {};
    let existingDesc = '';
    let collectionIdForDesc = null;

    if (
      Array.isArray(userCard.collections) &&
      userCard.collections.length > 0
    ) {
      let matched = null;
      if (activeCollectionId !== null && activeCollectionId !== undefined) {
        matched = userCard.collections.find((c) => c.id === activeCollectionId);
      }
      if (!matched) {
        matched = userCard.collections[0];
      }
      existingDesc = matched.description || '';
      collectionIdForDesc = matched.id;
    } else {
      existingDesc = userCard.description || '';
      if (userCard.collection_id) {
        collectionIdForDesc = userCard.collection_id;
      } else if (
        Array.isArray(userCard.collection_ids) &&
        userCard.collection_ids.length > 0
      ) {
        collectionIdForDesc = userCard.collection_ids[0];
      }
    }

    const descArea = this.createDescriptionArea(
      existingDesc,
      collectionIdForDesc,
      userCard.user_card_id || userCard.id,
      headers
    );
    descContainer.appendChild(descArea);
    descMsgContainer.appendChild(descContainer);

    const priceBadge = document.createElement('div');
    const hasPrice = bestPrice !== null && bestPrice !== undefined;
    priceBadge.classList.add('price-badge');
    if (hasPrice) {
      priceBadge.textContent = `${
        priceSource === 'buyNow' ? '< ' : ''
      }${this.formatPrice(bestPrice)}`;
    } else {
      priceBadge.textContent = 'N/A';
      priceBadge.classList.add('price-badge-no-price');
    }
    priceBadge.addEventListener('click', (event) => {
      event.stopPropagation();
      const compsCount =
        (matchedItems ? matchedItems.length : 0) +
        (pricedItems ? pricedItems.length : 0);
      this.showInfoModal(
        cardInfo,
        compsCount,
        bestPrice,
        priceSource,
        pricedItems,
        matchedItems
      );
    });
    descMsgContainer.appendChild(priceBadge);

    contentContainer.appendChild(descMsgContainer);

    cardDiv.appendChild(contentContainer);

    const searchTerms = [
      cardInfo.reference_card.players.join(' '),
      cardInfo.reference_card.year,
      cardInfo.reference_card.program,
      cardInfo.reference_card.release_set,
      cardInfo.reference_card.parallel || '',
      cardInfo.reference_card.number || '',
      teamToShow,
      positionToShow,
      existingDesc || '',
    ]
      .join(' ')
      .toLowerCase();
    cardDiv.dataset.searchable = searchTerms;

    logMessage(
      LOG_LEVELS.INFO,
      'Card element created and event listener attached',
      { cardId: cardInfo.reference_card.id }
    );

    return cardDiv;
  }

  filterCards(query) {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    document.querySelectorAll('.card').forEach((el) => {
      const data = el.dataset.searchable || '';
      const visible = terms.every((t) => data.includes(t));
      el.style.display = visible ? '' : 'none';
    });
  }

  showInfoModal(
    cardInfo,
    compsCount,
    bestPrice,
    priceSource = null,
    pricedItems = [],
    matchedItems = []
  ) {
    const modalBackdrop = document.createElement('div');
    modalBackdrop.classList.add('message-modal-backdrop');

    const modalContainer = document.createElement('div');
    modalContainer.classList.add('message-modal-container');

    const modalHeader = document.createElement('div');
    modalHeader.classList.add('message-modal-header');

    const modalTitle = document.createElement('h2');
    modalTitle.textContent = 'Card Details';
    modalHeader.appendChild(modalTitle);

    const closeButton = document.createElement('button');
    closeButton.classList.add('message-modal-close');
    closeButton.innerHTML = '×';
    closeButton.addEventListener('click', () => modalBackdrop.remove());
    modalHeader.appendChild(closeButton);

    const modalBody = document.createElement('div');
    modalBody.classList.add('message-modal-body');

    const details = document.createElement('div');
    details.classList.add('message-modal-card-info');
    const ref = cardInfo.reference_card;
    const user = cardInfo.user_card || {};
    let infoHTML = `
      <div><strong>Player:</strong> ${ref.players.join(', ')}</div>
      <div><strong>Year:</strong> ${ref.year}</div>
      <div><strong>Manufacturer:</strong> ${ref.manufacturer || 'N/A'}</div>
      <div><strong>Set:</strong> ${ref.program} ${ref.release_set}</div>
    `;
    if (ref.parallel) {
      infoHTML += `<div><strong>Parallel:</strong> ${ref.parallel}</div>`;
    }
    if (ref.number) {
      infoHTML += `<div><strong>Numbered:</strong> ${ref.number}</div>`;
    }
    if (user.condition) {
      infoHTML += `<div><strong>Condition:</strong> ${user.condition}</div>`;
    }
    if (ref.grading_company && ref.grade) {
      infoHTML += `<div><strong>Grade:</strong> ${ref.grading_company} ${ref.grade}</div>`;
    }
    details.innerHTML = infoHTML;
    modalBody.appendChild(details);

    const compsDiv = document.createElement('div');
    compsDiv.classList.add('info-comps');
    const compsHeader = document.createElement('div');
    compsHeader.textContent = `Comps Found: ${compsCount}`;
    compsDiv.appendChild(compsHeader);

    if (matchedItems.length > 0) {
      const activeList = document.createElement('ul');
      activeList.classList.add('comps-list');
      matchedItems.slice(0, 5).forEach((item) => {
        const li = document.createElement('li');
        const price = getItemPrice(item);
        if (price !== null) {
          const priceSpan = document.createElement('span');
          priceSpan.textContent = `${this.formatPrice(price)} - `;
          li.appendChild(priceSpan);
        }

        const link = document.createElement('a');
        link.href = item.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = item.title;
        li.appendChild(link);

        activeList.appendChild(li);
      });
      const activeHeader = document.createElement('div');
      activeHeader.textContent = 'Active Listings:';
      compsDiv.appendChild(activeHeader);
      compsDiv.appendChild(activeList);
    }

    if (pricedItems.length > 0) {
      const list = document.createElement('ul');
      list.classList.add('comps-list');
      pricedItems.slice(0, 5).forEach((item) => {
        const li = document.createElement('li');
        const price = getItemPrice(item);
        if (price !== null) {
          const priceSpan = document.createElement('span');
          priceSpan.textContent = `${this.formatPrice(price)} - `;
          li.appendChild(priceSpan);
        }

        const link = document.createElement('a');
        link.href = item.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = item.title;
        li.appendChild(link);

        list.appendChild(li);
      });
      const soldHeader = document.createElement('div');
      soldHeader.textContent = 'Completed Transactions:';
      compsDiv.appendChild(soldHeader);
      compsDiv.appendChild(list);
    }

    modalBody.appendChild(compsDiv);

    const priceDiv = document.createElement('div');
    priceDiv.textContent =
      bestPrice !== null && bestPrice !== undefined
        ? `Estimated Price: ${
            priceSource === 'buyNow' ? '< ' : ''
          }${this.formatPrice(bestPrice)}`
        : 'Estimated Price: N/A';
    modalBody.appendChild(priceDiv);

    const modalFooter = document.createElement('div');
    modalFooter.classList.add('message-modal-footer');

    modalContainer.appendChild(modalHeader);
    modalContainer.appendChild(modalBody);
    modalContainer.appendChild(modalFooter);

    modalBackdrop.appendChild(modalContainer);

    modalBackdrop.addEventListener('click', (e) => {
      if (e.target === modalBackdrop) {
        modalBackdrop.remove();
      }
    });

    document.body.appendChild(modalBackdrop);
  }

  /**
   * Appends a temporary card with a local image while the server processes the new card.
   * @param {string} localImageUrl - The locally created object URL for the card image.
   */
  appendPlaceholderCard(localImageUrl) {
    const grid = document.querySelector(this.gridSelector);
    if (!grid) return;

    const cardDiv = document.createElement('div');
    cardDiv.classList.add('card', 'card-placeholder');

    const img = document.createElement('img');
    img.src = localImageUrl;
    img.alt = 'Processing card...';
    cardDiv.appendChild(img);

    const statusDiv = document.createElement('div');
    statusDiv.classList.add('card-status-not-found');
    statusDiv.textContent = 'PROCESSING…';
    cardDiv.appendChild(statusDiv);

    grid.appendChild(cardDiv);
  }
}

export { CardGrid };
